<template>
  <owal-modal-panel
    slot="content"
    :title="`Find more tags for ${this.name}?`"
    :bottomButtons="bottomButtons"
    class="owal-match-finder-config__modal-container"
    @button-click="handleButtonClick"
    >
    <camera-picker
      :show="showCameraPicker"
      @close="showCameraPicker = false"
      @change="updateCameraId"
      />
    <image-selector-panel
      v-show="showSourcePhotoPicker"
      :sourceImages="potentialSourcePhotos"
      title="Select a photo for matching."
      :loading="loading"
      @close="() => showSourcePhotoPicker = false"
      @selection-complete="selectSourcePhoto"
      />
    <image-selector-panel
      v-show="showMatchPicker"
      :sourceImages="matchFoundImages"
      title="Select images for training."
      :multipicker="true"
      :loading="loading"
      @close="closeMatchPicker"
      @selection-complete="saveImages"
      />
    <div
      class="owal-match-finder-config__selectors-panel"
      style="height: 400px;"
      >
      <div class="owal-match-finder-config__two-column-container">
        <div class="owal-match-finder-config__left-column">
          From:
        </div>
        <owal-date-time-input
          style="min-width:fit-content;"
          id="from"
          type="datetime"
          :value="fromDate"
          :changeFunction="updateDateFilter"
          />
      </div>
      <div class="owal-match-finder-config__two-column-container">
        <div class="owal-match-finder-config__left-column">
          To:
        </div>
        <owal-date-time-input
          style="min-width:fit-content;"
          id="to"
          type="datetime"
          :value="toDate"
          :changeFunction="updateDateFilter"
          />
      </div>
      <div class="owal-match-finder-config__two-column-container">
        <div class="owal-match-finder-config__left-column">
          Camera:
        </div>
        <span
          class="owal-training-tool-filter picker container"
          @click="showCameraPicker = true"
          >
          <span
            class="owal-training-tool-filter picker value"
            >
            {{ cameraName || 'Any Camera' }}
          </span>
        </span>
        <span
          class="owal-training-tool-filter__picker-auxiliary-button"
          @click="updateCameraId(null)"
          >
          Any
        </span>
      </div>
      <div class="owal-match-finder-config__two-column-container">
        <div class="owal-match-finder-config__left-column">
          Source Image:
        </div>
        <div
          class="owal-match-finder-config__photo-selector"
          style="display: flex; flex-direction: column;"
          >
          <img
            v-if="sourcePhotoId || uploadImage"
            :src="sourcePhotoId ? sourceImageURL(sourcePhotoId) : uploadImage.data_b64"
            class="owal-match-finder-config__selected-source-image"
            >
          <div
            v-else
            style="display: flex; justify-content: center; align-items: center;"
            >
            <owal-logo />
          </div>
          <div style="display: flex; flex-direction: row; padding-top: .5rem;">
            <owal-file-input identifier="file-upload-input" :plain="true" style="padding-right: .5rem" :showLabel="false" @input="setImage"/>
            <owal-button @click="showSourcePhotoPicker = true"> Select Confirmed Match </owal-button>
          </div>
        </div>
      </div>
    </div>
  </owal-modal-panel>
</template>
<script>
import { lazyLoad } from '@/lazy'

import OwalModalPanel from '@/libraries/owal-ui/panels/OwalModalPanel'
import { mapGetters } from 'vuex'
import OwalDateTimeInput from '@/libraries/owal-ui/inputs/OwalDateTimeInput'
import { facesconfirmed, facematchconfirm, fetchMatchImages, imageForDetectionID } from '@/libraries/api/faces'
import { OwalLogo, OwalFileInput } from '@/libraries/owal-ui'
import OwalButton from '@/libraries/owal-ui/buttons/OwalButton'
import moment from 'moment'
import ImageSelectorPanel from './ImageSelectorPanel'
const CameraPicker = lazyLoad('shared/CameraPicker')

export default {
  name: 'MatchFinderConfigurationPanel',
  props: {
    /**
     * @prop {String} name The name of the identity used to match search.
     * */
    name: {
      type: String,
      required: true,
    },
    /**
     * @prop {String} frontendFaceUUID the frontend face UUID
     * */
    frontendFaceUUID: {
      type: String,
      required: true,
    },
  },
  data () {
    return {
      bottomButtons: [{
        title: 'Search',
        identifier: 'confirm',
      },
      {
        title: 'Cancel',
        identifier: 'close',
      }],
      showMatchPicker: false,
      fromDate: undefined,
      toDate: undefined,
      datetimeFormat: 'YYYY-MM-DDTHH:mm',
      showCameraPicker: false,
      selectedCameraId: undefined,
      sourcePhotoId: undefined,
      showSourcePhotoPicker: false,
      matchFoundImages: [],
      loading: false,
      uploadImage: undefined,
      potentialSourcePhotos: [],
    }
  },
  methods: {
    handleButtonClick (identifier) {
      if (identifier === 'close') {
        this.$emit('close')
      } else if (identifier === 'confirm') {
        this.getMatches()
      }
    },
    updateCameraId (cameraId) {
      this.selectedCameraId = cameraId
      this.showCameraPicker = false
    },
    sourceImageURL (detectionId) {
      return imageForDetectionID(detectionId)
    },
    selectSourcePhoto (imageId) {
      if (this.uploadImage) {
        this.uploadImage = undefined
      }
      this.sourcePhotoId = imageId
      this.showSourcePhotoPicker = false
    },
    getMatches () {
      return new Promise(function (resolve, reject) {
        const options = {
          detectionId: this.sourcePhotoId || undefined,
          searchStart: this.fromDate || undefined,
          searchEnd: this.toDate || undefined,
          cameraUUID: this.selectedCameraId || undefined,
          domainUUID: this.currentDomainUUID,
        }
        if (this.uploadImage) {
          options.image = this.uploadImage.blob
        }
        this.loading = true
        this.showMatchPicker = true

        fetchMatchImages(options)
          .then(response => {
            this.loading = false
            if (response.data && response.data.ok) {
              this.matchFoundImages = response.data.results.map(result => {
                if (result.detection_id) {
                  result.imageURL = imageForDetectionID(result.detection_id)
                }
                return result
              })
            } else {
              alert(response.data.error || 'No data received. Please cancel and try again.')
              reject(Error(`Error: ${response.data.err}`))
            }
          })
      }.bind(this))
    },
    updateDateFilter (target) {
      if (target.id === 'from') {
        this.fromDate = moment.utc(target.value, 'YYYY-MM-DDTHH:mmZZ').format(this.datetimeFormat)
      } else if (target.id === 'to') {
        this.toDate = moment.utc(target.value, 'YYYY-MM-DDTHH:mmZZ').format(this.datetimeFormat)
      }
    },
    saveImages (images) {
      this.showMatchPicker = false
      facematchconfirm(images, this.frontendFaceUUID, true, this.currentDomainUUID)
    },
    closeMatchPicker () {
      this.showMatchPicker = false
      this.loading = false
    },
    setImage (incoming) {
      if (this.sourcePhotoId) {
        this.sourcePhotoId = undefined
      }

      this.uploadImage = incoming
    },
  },
  computed: {
    ...mapGetters({
      cameraForId: 'cameraForUUID',
      currentDomainUUID: 'currentDomainUUID',
      authToken: 'authToken',
    }),
    cameraName () {
      let name
      if (this.selectedCameraId) {
        const matchingCamera = this.cameraForId(this.selectedCameraId)

        if (matchingCamera) {
          name = matchingCamera.description || `Unknown (${this.selectedCameraId})`
        }
      }
      return name
    },
  },
  mounted () {
    const now = moment().utc()
    this.toDate = now.subtract(1, 'hours').format(this.datetimeFormat)
    this.fromDate = now.subtract(2, 'hours').format(this.datetimeFormat)
    facesconfirmed(this.currentDomainUUID, 0, 100000, this.frontendFaceUUID).then(res => res.data).then(function (data) {
      if (!data.ok) {
        console.log(data.err)
        return
      }
      this.loading = false
      this.potentialSourcePhotos = data.faces_confirmed.map(result => {
        result.imageURL = imageForDetectionID(result.detection_id)
        return result
      })
    }.bind(this)).catch((err) => {
      console.log(err)
      this.loading = false
      return []
    })
  },
  components: {
    OwalModalPanel,
    OwalDateTimeInput,
    CameraPicker,
    OwalLogo,
    ImageSelectorPanel,
    OwalFileInput,
    OwalButton,
  },
}
</script>

<style scoped>
  .owal-match-finder-config__two-column-container {
    width: 100%;
    display: flex;
    flex-direction: row;
    justify-content: flex-start;
    align-items: center;
  }

  .owal-match-finder-config__selectors-panel {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
  }

  .owal-match-finder-config__left-column {
    min-width: 15%
  }

  .owal-match-finder-config__modal-container {
    height: 100%;
  }

  .owal-match-finder-config__photo-selector-container {
    height: 150%;
  }

  .owal-match-finder-config__modal-container >>> .owal.panel.active.window {
    height: 36.5rem!important;
    max-height: 80%!important;
    width: 80%!important;
  }

  /* From OWAL Training Tool */
  .owal-training-tool-filter.picker.container,
  .owal-training-tool-filter.container input,
  .owal-training-tool-filter.container select {
    background: #F0F0F0;
    border: 2px solid #E0E0E0;
    border-radius: 5px;

    /* font-family: 'Lato-Regular', sans-serif;
    font-size: 1.28571rem;
    color: #324057;
    letter-spacing: 0; */
    padding: 0.87571rem 0.64285rem;
    margin: 0.2857142857rem .1785714286rem;
    height: 3.6rem;
    flex-grow: 0;
    text-align: center;
  }

  .owal-training-tool-filter__input label {
    text-align: right;
    width: 10rem;
    flex-shrink: 0;
    box-sizing: border-box;
    margin: 0.2857142857rem .1785714286rem;
  }

  .owal-training-tool-filter.picker.value {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    text-align: center;
    flex-grow: 1;
    flex-shrink: none;
  }

.owal-training-tool-filter.picker.container {
    width: 13.5rem;
    display: flex;
    flex-direction: row;
    justify-content: flex-start;
    cursor: pointer;
  }

  .owal-training-tool-filter__picker-auxiliary-button {
    font-size: 0.9rem;
    transition: all 300ms ease;
    text-align: center;
    flex-shrink: 0;
    font-family: 'Lato', 'Avenir', sans-serif;
    background-color: rgb(183, 189, 197);
    color: #F3F4F5;
    border: 2px solid #F3F4F5;
    padding: 0.25rem 0.35rem;
    margin: 0 0.5rem;
    border-radius: 0.5rem;
    cursor: pointer;
    font-weight: bold;
  }

  .owal-floating-logo__container {
    padding: 1.5rem;
    align-content: center;
    align-items: center;
    justify-content: center;
    display: flex;
    flex-direction: column;
    flex-grow: 1;
    width: 100%;
    height: 100%;
    background: #EFF2F7;
  }

  .owal-match-finder-config__selected-source-image {
    height: 150px;
    width: 150px;
    border-radius: .6rem;
    border: .03rem solid gray;
    object-fit:cover;
  }

  .owal-match-finder-config__photo-selector{
    padding: 1.5rem;
    cursor: pointer;
  }
  </style>
